import i18n from "./i18n";
import ApiService from "../../../share/src/util/api/service";
import handler from '../../../share/src/util/apiResultValidator';
import logWrapper from '../../../share/src/logWrapper';

const loadedResourceKeys = new Set(); // 取得済みのpack.jsonのキー一覧

function getResourceKey(region, language) {
  return `${region ?? ''}:${language}`;
}

class i18nResourceLoader {
  // 進行中のデータ取得Promiseを保持する静的フィールド
  static #fetchPromises = new Map();

  /**
   * i18nリソースの取得を開始する。すでに取得中の場合は既存のPromiseを返す
   */
  static start(region, language) {
    return this.fetchResource(region, language);
  }

  /**
   * 現変更時にi18nリソースの取得を開始する。すでに変更中の場合は既存のPromiseを返す
   */
  static changeLanguage(region, language) {
    return this.fetchResource(region, language);
  }

  /**
   * i18nの取得完了するまで待機する
   */
  static async wait() {
    if (this.#fetchPromises.size === 0) {
      return null;
    }

    const promises = Array.from(this.#fetchPromises.values());
    return await Promise.all(promises);
  }

  static fetchResource(region, language) {
    const resourceKey = getResourceKey(region, language);
    if (loadedResourceKeys.has(resourceKey)) {
      return null;
    }

    const existingPromise = this.#fetchPromises.get(resourceKey);
    if (existingPromise) {
      return existingPromise;
    }

    const fetchPromise = (async () => {
      try {
        const packJson = await loadI18nResource(region, language);
        if (!packJson) {
          return null;
        }

        const i18nGlobal = i18n.global;
        const current = i18nGlobal.getLocaleMessage(language); // 既存i18nリソース

        // 取得したjsonファイルのうち、新規IDのみを現在のi18nにマージする
        const merged = addOnlyNew(structuredClone(current), packJson);
        i18nGlobal.setLocaleMessage(language, merged);
        loadedResourceKeys.add(resourceKey);
        return merged;
      } catch (error) {
        logWrapper.log("データ取得失敗:", error);
        return null;
      } finally {
        this.#fetchPromises.delete(resourceKey);
      }
    })();

    this.#fetchPromises.set(resourceKey, fetchPromise);
    return fetchPromise;
  }
}

/**
 * i18nリソースをロードする
 * @param {*} region リージョン情報（master or mirror） 保存データ画面以外からは渡されない
 * @param {*} language 言語情報
 * @returns i18nリソース
 */
async function loadI18nResource(region, language) {
  // 取得したリソースファイルはキャッシュストレージに保存し、Etagを比較して再取得の判断をする
  const CACHE_NAME = `i18n-${region}-${language}`;
  const STABLE_KEY = '/i18n/resource'; // キャッシュストレージ内のキー
  const cache = await caches.open(CACHE_NAME);
  const cachedResp = await cache.match(STABLE_KEY);
  // 既存ETagの抽出
  const prevEtag = cachedResp?.headers.get('ETag') || null;

  // プレサインドURLを取得
  const param = `/resources/languagePackLink?lang=${language}&region=${region}`;
  const getI18nPresignedUrl = await ApiService.getCall(ApiService.MAINT, param);
  handler.validate(handler.validateTypes.all, getI18nPresignedUrl, null, null,
    () => { }, () => { }, null, false); // APIに失敗しても、エラー時のアラート表示はなし
  const presignedUrl = getI18nPresignedUrl.data.url;
  if (!presignedUrl) {
    return null;
  }

  // 条件付きリクエストのヘッダー構築
  const headers = new Headers();
  if (prevEtag) headers.set('If-None-Match', prevEtag);

  // ブラウザのHTTPキャッシュは使わず、明示的に再検証を行うため no-store
  let networkResp;
  try {
    networkResp = await fetch(presignedUrl, {
      method: 'GET',
      headers,
      cache: 'no-store',
    });
  } catch (e) {
    // ネットワーク失敗→キャッシュがあればそれを返す
    if (cachedResp) {
      return parseJsonSafe(cachedResp);
    }
    throw e;
  }

  if (networkResp.status === 304 && cachedResp) {
    // 変更がない場合キャッシュから返す
    return parseJsonSafe(cachedResp);
  }
  if (networkResp.ok) {
    // 新規または更新があった場合は保存し直す
    await cache.put(STABLE_KEY, networkResp.clone());
    return parseJsonSafe(networkResp);
  }
  // 失敗時フォールバック
  if (cachedResp) return parseJsonSafe(cachedResp);
  throw new Error(`Failed to fetch resource: ${networkResp.status}`);
}

/**
 * JSONに変換する
 * @param {*} resp レスポンスオブジェクト
 * @returns 
 */
async function parseJsonSafe(resp) {
  return await resp.json();
}

/**
 * 既存のi18nリソースに新しいリソースを追加する（既存のキーは上書きしない）
 * @param {*} current 既存のi18nリソース
 * @param {*} packJson 新しいi18nリソース
 * @returns マージ後のi18nリソース
 */
function addOnlyNew(current, packJson) {
  if (!packJson || typeof packJson !== 'object') return current;
  for (const key of Object.keys(packJson)) {
    // 既存i18nにキーが無いなら追加
    if (current[key] === undefined) {
      current[key] = packJson[key];
    }
    // 両方がオブジェクトなら再帰
    const packObj = packJson[key] && typeof packJson[key] === 'object';
    const currentObj = current[key] && typeof current[key] === 'object';
    if (packObj && currentObj) {
      addOnlyNew(current[key], packJson[key]);
    }
  }
  return current;
}

export default i18nResourceLoader;